import { useId, useState } from "react";
import { format, formatISO } from "date-fns";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { getActivityDescriptionParts } from "@components/history/utils/getActivityDescription";
import type { HistoryRow } from "@components/history/views/historyViewConfig";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { timeAgo } from "@helpers/date";
import { ChevronRightSmallIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import { WorkActivityActorAvatar } from "./WorkActivityActorAvatar";
import { WorkActivityChanges } from "./WorkActivityChanges";
import styles from "./WorkActivity.module.scss";

interface WorkActivityLineProps {
    row: HistoryRow;
    closeId: string;
    workspaceId: string;
    /** Set on lines "Load more" added: their place in that batch, for the stagger. */
    staggerIndex: number | undefined;
}

/**
 * One grouped activity line on the timeline: who changed it (their avatar),
 * what changed and when. The whole row expands in place to list each changed
 * task with its before and after.
 */
export const WorkActivityLine = ({
    row,
    closeId,
    workspaceId,
    staggerIndex,
}: Readonly<WorkActivityLineProps>) => {
    const changesId = useId();
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasExpanded, setHasExpanded] = useState(false);
    const { prefix, counter, suffix } = getActivityDescriptionParts(row);
    const toggle = () => {
        setIsExpanded(!isExpanded);
        setHasExpanded(true);
    };

    return (
        <li
            className={styles.line}
            data-staggered={staggerIndex !== undefined || undefined}
            style={
                staggerIndex === undefined
                    ? undefined
                    : styleWithVars({ "--stagger-index": staggerIndex })
            }
        >
            <button
                type="button"
                className={styles.toggle}
                aria-expanded={isExpanded}
                aria-controls={changesId}
                onClick={toggle}
            >
                <span className={styles.avatar}>
                    <WorkActivityActorAvatar row={row} />
                </span>
                <span className={styles.text}>
                    {prefix}
                    {counter && (
                        <span className={styles.counter}> {counter}</span>
                    )}
                    {suffix && ` ${suffix}`}
                    <span className={styles.caret} aria-hidden>
                        <ChevronRightSmallIcon />
                    </span>
                </span>
                <Tooltip content={format(row.date, fullDateFormatWithTime)}>
                    <time
                        className={styles.time}
                        dateTime={formatISO(row.date)}
                    >
                        {timeAgo(row.date)}
                    </time>
                </Tooltip>
            </button>
            <div
                id={changesId}
                className={styles.changesRow}
                data-expanded={isExpanded || undefined}
            >
                <div className={styles.changesClip}>
                    {hasExpanded && (
                        <WorkActivityChanges
                            row={row}
                            closeId={closeId}
                            workspaceId={workspaceId}
                        />
                    )}
                </div>
            </div>
        </li>
    );
};
